import { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, X, Save, Search, User, Mail, Phone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TYPES = [
  { value: "chauffeur", label: "Chauffeur", color: "bg-blue-100 text-blue-800" },
  { value: "decoratrice", label: "Décoratrice", color: "bg-pink-100 text-pink-800" },
  { value: "autre", label: "Autre", color: "bg-gray-100 text-gray-700" },
];

const EMPTY_FORM = { nom: "", email: "", telephone: "", type: "chauffeur", notes: "" };

export default function AdminContacts() {
  const { authHeaders } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [showModal, setShowModal] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [deleting, setDeleting] = useState(null);

  const fetchContacts = useCallback(() => {
    setLoading(true);
    axios.get(`${API}/contacts`, { headers: authHeaders })
      .then(r => setContacts(r.data))
      .finally(() => setLoading(false));
  }, [authHeaders]);

  useEffect(() => { fetchContacts(); }, [fetchContacts]);

  const filtered = contacts.filter(c => {
    const matchSearch = !search || c.nom.toLowerCase().includes(search.toLowerCase()) || c.email.toLowerCase().includes(search.toLowerCase());
    const matchType = typeFilter === "all" || c.type === typeFilter;
    return matchSearch && matchType;
  });

  const openCreate = () => { setEditingContact(null); setForm(EMPTY_FORM); setError(""); setShowModal(true); };
  const openEdit = (c) => { setEditingContact(c); setForm({ nom: c.nom, email: c.email, telephone: c.telephone || "", type: c.type, notes: c.notes || "" }); setError(""); setShowModal(true); };
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.nom.trim() || !form.email.trim()) { setError("Nom et email sont obligatoires"); return; }
    setSaving(true); setError("");
    try {
      if (editingContact) {
        await axios.put(`${API}/contacts/${editingContact.id}`, form, { headers: authHeaders });
      } else {
        await axios.post(`${API}/contacts`, form, { headers: authHeaders });
      }
      fetchContacts();
      setShowModal(false);
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally { setSaving(false); }
  };

  const deleteContact = async (id) => {
    await axios.delete(`${API}/contacts/${id}`, { headers: authHeaders });
    setDeleting(null);
    fetchContacts();
  };

  const getTypeInfo = (type) => TYPES.find(t => t.value === type) || TYPES[2];

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Contacts</h1>
          <p className="text-sm text-gray-500 mt-1">Chauffeurs et décoratrices assignés aux mariages</p>
        </div>
        <button onClick={openCreate} data-testid="create-contact-btn"
          className="flex items-center gap-2 px-4 py-2 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] transition-colors rounded">
          <Plus size={16} /> Nouveau contact
        </button>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-5">
        <div className="relative flex-1 max-w-xs">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher..."
            data-testid="search-contacts"
            className="w-full pl-8 pr-3 py-2 border border-gray-200 text-sm focus:outline-none focus:border-[#D4A843]" />
        </div>
        <div className="flex gap-1">
          {[{ value: "all", label: "Tous" }, ...TYPES].map(t => (
            <button key={t.value} onClick={() => setTypeFilter(t.value)}
              className={`px-3 py-2 text-sm font-medium transition-colors rounded ${typeFilter === t.value ? "bg-[#1B2E4B] text-white" : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-[#D4A843] border-t-transparent rounded-full animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-16 text-gray-400">
          <User size={40} className="mx-auto mb-3 opacity-30" />
          <p className="font-medium">Aucun contact trouvé</p>
          <p className="text-sm mt-1">Ajoutez vos chauffeurs et décoratrices</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(c => {
            const typeInfo = getTypeInfo(c.type);
            return (
              <div key={c.id} data-testid={`contact-card-${c.id}`}
                className="bg-white border border-gray-100 p-4 shadow-sm hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between mb-3">
                  <div>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-sm ${typeInfo.color}`}>{typeInfo.label}</span>
                    <h3 className="font-bold text-gray-900 mt-2 text-lg">{c.nom}</h3>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => openEdit(c)} data-testid={`edit-contact-${c.id}`}
                      className="p-1.5 text-gray-400 hover:text-[#D4A843] transition-colors"><Pencil size={14} /></button>
                    <button onClick={() => setDeleting(c.id)} data-testid={`delete-contact-${c.id}`}
                      className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"><Trash2 size={14} /></button>
                  </div>
                </div>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2 text-sm text-gray-600">
                    <Mail size={13} className="text-gray-400 flex-shrink-0" />
                    <span className="truncate">{c.email}</span>
                  </div>
                  {c.telephone && (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <Phone size={13} className="text-gray-400 flex-shrink-0" />
                      <span>{c.telephone}</span>
                    </div>
                  )}
                  {c.notes && <p className="text-xs text-gray-400 mt-2 italic">{c.notes}</p>}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Delete confirmation */}
      {deleting && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center">
          <div className="bg-white p-6 shadow-2xl max-w-sm w-full mx-4">
            <h3 className="font-bold text-lg mb-2">Supprimer ce contact ?</h3>
            <p className="text-sm text-gray-500 mb-5">Cette action est irréversible.</p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setDeleting(null)} className="px-4 py-2 border border-gray-200 text-sm font-semibold">Annuler</button>
              <button onClick={() => deleteContact(deleting)} data-testid="confirm-delete-contact"
                className="px-5 py-2 bg-red-500 text-white text-sm font-semibold hover:bg-red-600">Supprimer</button>
            </div>
          </div>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md shadow-2xl">
            <div className="flex items-center justify-between p-5 border-b border-gray-100">
              <h2 className="font-bold text-gray-900">{editingContact ? "Modifier le contact" : "Nouveau contact"}</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-5 space-y-4">
              {error && <p className="text-red-500 text-sm bg-red-50 p-2 rounded">{error}</p>}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Type *</label>
                <div className="flex gap-2">
                  {TYPES.map(t => (
                    <button key={t.value} type="button" onClick={() => set("type", t.value)}
                      className={`flex-1 py-2 text-sm font-semibold border transition-colors rounded ${form.type === t.value ? "bg-[#1B2E4B] text-white border-[#1B2E4B]" : "border-gray-200 text-gray-600 hover:bg-gray-50"}`}>
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>
              {[["nom","Nom *","text",true],["email","Email *","email",true],["telephone","Téléphone","tel",false]].map(([k,l,t,r]) => (
                <div key={k}>
                  <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">{l}</label>
                  <input type={t} value={form[k]} onChange={e => set(k, e.target.value)} required={r}
                    data-testid={`contact-${k}`}
                    className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843]" />
                </div>
              ))}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1 uppercase tracking-wider">Notes</label>
                <textarea value={form.notes} onChange={e => set("notes", e.target.value)} rows={2}
                  data-testid="contact-notes"
                  className="w-full border border-gray-200 px-3 py-2.5 text-sm focus:outline-none focus:border-[#D4A843] resize-none" />
              </div>
              <div className="flex gap-3 pt-2 justify-end">
                <button type="button" onClick={() => setShowModal(false)}
                  className="px-4 py-2 border border-gray-200 text-sm font-semibold hover:bg-gray-50">Annuler</button>
                <button type="submit" disabled={saving} data-testid="save-contact-btn"
                  className="flex items-center gap-2 px-5 py-2 bg-[#1B2E4B] text-white text-sm font-semibold hover:bg-[#162440] disabled:opacity-60 rounded">
                  <Save size={14} /> {saving ? "Sauvegarde..." : "Enregistrer"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
